/**
 * Newsletter dialog: sends to the leads matched by the CURRENT list filters.
 * Mirrors the legacy dialog: recipient funnel counters, opt-in-only toggle
 * (default on, GDPR), template selector (marketing designs + dynamic
 * templates) or inline compose, confirmation and result report.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useListContext, useNotify, useTranslate } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import queryString from 'query-string';
import { httpClient } from '@/utils/httpClient';

interface TemplateOption {
  id: string;
  name: string;
  kind: 'design' | 'template';
}

interface Preview {
  total: number;
  withEmail: number;
  optIn: number;
}

interface SendResult {
  sent: number;
  failed: number;
  skippedNoEmail: number;
  errors: string[];
}

/** Flattens react-admin filter values into LeadListQuery-shaped params. */
const toFilterPayload = (filterValues: Record<string, unknown>) => {
  const payload: Record<string, unknown> = {};
  Object.entries(filterValues).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    payload[key] = Array.isArray(value) ? value.join(',') : value;
  });
  return payload;
};

export const NewsletterDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { filterValues } = useListContext();

  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`resources.leads.newsletter.${key}`, options);

  const [preview, setPreview] = useState<Preview | null>(null);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [onlyOptIn, setOnlyOptIn] = useState(true);
  const [templateKey, setTemplateKey] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);

  const filterPayload = useMemo(() => toFilterPayload(filterValues), [filterValues]);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    httpClient
      .get<Preview>(`/newsletters/recipients-preview?${queryString.stringify(filterPayload)}`)
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null));
    httpClient
      .get<{ designs: { id: string; name: string }[]; dynamicTemplates: { id: string; name: string }[] }>(
        '/newsletters/templates'
      )
      .then((r) =>
        setTemplates([
          ...r.data.designs.map((d): TemplateOption => ({ ...d, kind: 'design' })),
          ...r.data.dynamicTemplates.map((d): TemplateOption => ({ ...d, kind: 'template' })),
        ])
      )
      .catch(() => setTemplates([]));
  }, [open, filterPayload]);

  const selectedTemplate = templates.find((tp) => `${tp.kind}:${tp.id}` === templateKey);
  const recipients = onlyOptIn ? preview?.optIn ?? 0 : preview?.withEmail ?? 0;
  const inlineMode = !selectedTemplate;

  const handleSend = async () => {
    if (recipients === 0) return;
    if (inlineMode && (!subject.trim() || !body.trim())) {
      notify(t('inlineRequired'), { type: 'warning' });
      return;
    }
    if (!window.confirm(t('confirm', {
      count: recipients,
      what: selectedTemplate ? selectedTemplate.name : subject,
    }))) {
      return;
    }

    setSending(true);
    try {
      const response = await httpClient.post<SendResult>('/newsletters/send', {
        filter: filterPayload,
        onlyOptIn,
        subject: inlineMode ? subject : null,
        bodyText: inlineMode ? body : null,
        designId: selectedTemplate?.kind === 'design' ? selectedTemplate.id : null,
        templateId: selectedTemplate?.kind === 'template' ? selectedTemplate.id : null,
      });
      setResult(response.data);
    } catch {
      notify(t('error'), { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('title')}</DialogTitle>
      <DialogContent dividers>
        {result ? (
          <Alert severity={result.failed > 0 ? 'warning' : 'success'}>
            {t('result', {
              sent: result.sent,
              failed: result.failed,
              skipped: result.skippedNoEmail,
            })}
            {result.errors.slice(0, 5).map((error) => (
              <Typography key={error} variant="caption" display="block">{error}</Typography>
            ))}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {preview
                ? t('funnel', {
                    total: preview.total,
                    withEmail: preview.withEmail,
                    optIn: preview.optIn,
                  })
                : t('loadingPreview')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={onlyOptIn}
                  onChange={(e) => setOnlyOptIn(e.target.checked)}
                />
              }
              label={t('onlyOptIn')}
              sx={{ mb: 2, display: 'block' }}
            />
            <TextField
              select
              fullWidth
              size="small"
              label={t('template')}
              value={templateKey}
              onChange={(e) => setTemplateKey(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">{t('noTemplate')}</MenuItem>
              {templates.map((tp) => (
                <MenuItem key={`${tp.kind}:${tp.id}`} value={`${tp.kind}:${tp.id}`}>
                  {tp.kind === 'design' ? '[Marketing] ' : '[Dynamic] '}{tp.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              label={t('subject')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={!inlineMode}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={8}
              size="small"
              label={t('body')}
              helperText={inlineMode ? t('placeholdersHelp') : undefined}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={!inlineMode}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          {result ? translate('ra.action.close') : translate('ra.action.cancel')}
        </Button>
        {!result && (
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleSend}
            disabled={sending || recipients === 0}
          >
            {t('send', { count: recipients })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NewsletterDialog;
